import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {Chart} from 'angular-highcharts';
import {PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {IntelligenceData} from '../../../models/intelligence-data.interface';
import {POM_TREND_BY_COUNTRY_COLORS} from '../../../../constants/charts';
import {MessageService} from '../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../core/services/auth.service';

@Component({
  selector: 'app-retail-vs-municipalities-vs-other-bar-chart',
  templateUrl: './retail-vs-municipalities-vs-other-bar-chart.component.html',
  styleUrls: ['./retail-vs-municipalities-vs-other-bar-chart.component.scss']
})
export class RetailVsMunicipalitiesVsOtherBarChartComponent implements OnInit, OnChanges {

  textBelow!: string;
  chart!: Chart;
  chartData!: SeriesOptionsType[];
  countryCodes: string[] = [];
  textBelowCode = 'intelligence.Country.RetailVsMunicipalitiesVsOther.textBelow';
  isAdmin!: boolean;

  @Input()
  title!: string;
  @Input()
  subtitle!: string;
  @Input()
  data!: [IntelligenceData[]];

  private static getCountryCodes(chartData: IntelligenceData[]): string[] {
    const chartCodes = chartData?.map(item => item.countryCode);
    return [...new Set(chartCodes)];
  }

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    this.messageService.getDialogInfo(this.textBelowCode).subscribe((data) => {
      if (data !== null) {
        this.textBelow = data.description;
      }
    });
  }

  ngOnChanges(): void {
    if (this.data) {
      this.countryCodes = RetailVsMunicipalitiesVsOtherBarChartComponent.getCountryCodes(this.data[0]);
      // highcharts
      this.chartData = this.convertToChartData();
      this.chart = new Chart({
        chart: {
          zoomType: 'xy',
          type: 'column',
          options3d: {
            enabled: true,
            alpha: 7,
            beta: 0,
            depth: 35,
            viewDistance: 15
          }
        },
        colors: POM_TREND_BY_COUNTRY_COLORS,
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        exporting: {
          enabled: true
        },
        plotOptions: {
          column: {
            dataLabels: {
              enabled: true,
              format: '{point.y}%'
            }
          }
        },
        tooltip: {
          formatter(): string[]|undefined {
            if (this.point.name) {
              const pointOptions = this.point as PointOptionsObject;
              return ['<div class="center"><b>' + this.point.name + '</b></div><br><br>',
                '- Value: ' + `${this.point.y ? this.point.y + '%' : 'no value'}` + '<br>',
                '- Total responses: ' + pointOptions.custom?.totalResponses + '<br>',
                '- Average: ' +  pointOptions.custom?.average.toFixed(2) + '%'
              ];
            }
            // If not null, use the default formatter
            // return tooltip.defaultFormatter.call(this, tooltip);
            return undefined;
          }
        },
        xAxis: {
          categories: this.countryCodes,
          title: {
            text: null
          }
        },
        yAxis: {
          labels: {
            format: '{value}%'
          }
        },
        series: this.chartData
      });
    }
  }

  editTextBelow(): void {
    this.router.navigate(['/admin/chart-labels'], {state: { code: this.textBelowCode }}).then(
      () => {
        // do nothing
      }
    );
  }

  private convertToChartData(): SeriesOptionsType[] {
    let averageRetail = 0, averageMunicipalities = 0, averageOther = 0;
    let totalRetail = 0, totalMunicipalities = 0, totalOther = 0;
    this.chart?.ref?.hideData();
    const chartData = [] as SeriesOptionsType[];
    let question = null;
    this.data?.forEach((item) => {
      if (item[0]) {
        // give first element data for question if no value to create all categories
        if (item[0]?.questionId == null) {
          question = item.find((value) => value.questionId != null);
          if (question !== undefined) {
            item[0].questionId = question.questionId;
            item[0].questionCode = question.questionCode;
            item[0].questionDescription = question.questionDescription;
          }
        }
        if (item[0].questionDescription?.includes('retail')) {
           const retailValuesWithouthZero = item.map(x => x.totalSum).filter(x => x && x !== 0);
           totalRetail = retailValuesWithouthZero.length;
           if (retailValuesWithouthZero.length > 0) {
              averageRetail = retailValuesWithouthZero.reduce((partialSum, x) => partialSum + x) / totalRetail;
           }

        } else if (item[0].questionDescription?.includes('municipal')) {
          const municipalValuesWithouthZero = item.map(x => x.totalSum).filter(x => x && x !== 0);
          totalMunicipalities = municipalValuesWithouthZero.length;
          if (municipalValuesWithouthZero.length > 0) {
            averageMunicipalities = municipalValuesWithouthZero.reduce((partialSum, x) => partialSum + x) / totalMunicipalities;
          }
        } else {
          const otherValuesWithouthZero = item.map(x => x.totalSum).filter(x => x && x !== 0);
          totalOther = otherValuesWithouthZero.length;
          if (otherValuesWithouthZero.length > 0) {
            averageOther = otherValuesWithouthZero.reduce((partialSum, x) => partialSum + x) / totalOther;
          }
        }
        chartData.push({
          name: item[0].questionDescription !== null && item[0].questionDescription?.includes('retail')
            ? 'Retail'
            : (item[0].questionDescription?.includes('municipal') ? 'Municipalities' : 'Other'),
          type: 'column',
          data: item.map(dataItem => {
            return {
              y: (dataItem.totalSum && dataItem.totalSum !== 0) ? Number(dataItem.totalSum) : undefined,
              name: dataItem.countryName + ' - ' + dataItem.year,
              custom: {
                totalResponses: dataItem.questionDescription !== null && dataItem.questionDescription?.includes('retail')
                                  ? totalRetail
                                  : (dataItem.questionDescription?.includes('municipal') ? totalMunicipalities : totalOther),
                average: dataItem.questionDescription !== null && dataItem.questionDescription?.includes('retail')
                            ? averageRetail
                            : (dataItem.questionDescription?.includes('municipal') ? averageMunicipalities : averageOther),
              }
            };
          })
        });
      }
    });
    return chartData;
  }

}
